/**
 * Crop timeline grouping utilities
 * Groups crops by species, location, or family for organized timeline display
 */

/**
 * Get group key from crop based on grouping strategy
 * @param {Object} crop - Crop object
 * @param {string} strategy - 'species' | 'location' | 'family'
 * @returns {string|null} Group key or null if crop should not be grouped
 */
export function getGroupKey(crop, strategy) {
  switch (strategy) {
    case 'species':
      return crop.species_name || null
    case 'location':
      return crop.organization_id || null
    case 'family':
      return crop.crop_details?.family_code || null
    default:
      return null
  }
}

/**
 * Get display name for group
 * @param {Object} crop - Representative crop from the group
 * @param {string} strategy - Grouping strategy
 * @returns {string} Display name for the group
 */
export function getGroupName(crop, strategy) {
  switch (strategy) {
    case 'species':
      return crop.species_name || 'Unknown Species'
    case 'location':
      // Look up organization name from joined data
      return crop.organization?.name || 'Unknown Location'
    case 'family':
      return crop.crop_details?.crop_family_types?.name || crop.crop_details?.family_code || 'Unknown Family'
    default:
      return 'Unknown Group'
  }
}

/**
 * Group crops by the specified strategy
 * Crops without a valid group key are returned as individual "groups"
 *
 * @param {Array} crops - Array of crop objects
 * @param {string} strategy - 'none' | 'species' | 'location' | 'family'
 * @returns {Array} Array of group objects
 */
export function groupCropsByStrategy(crops, strategy = 'species') {
  if (!crops || crops.length === 0) {
    return []
  }

  // Handle "none" strategy - all crops in a single ungrouped container
  if (strategy === 'none') {
    return [{
      groupId: 'ungrouped-all',
      groupName: null,
      groupType: null,
      crops: crops,
      isUngrouped: true
    }]
  }

  // Group crops by key
  const grouped = new Map()
  const ungrouped = []

  for (const crop of crops) {
    const key = getGroupKey(crop, strategy)

    if (key === null) {
      // Crops without valid group key remain ungrouped (user requirement)
      ungrouped.push({
        groupId: `ungrouped-${crop.id}`,
        groupName: null,
        groupType: null,
        crops: [crop],
        isUngrouped: true
      })
    } else {
      if (!grouped.has(key)) {
        grouped.set(key, {
          groupId: key,
          groupName: getGroupName(crop, strategy),
          groupType: strategy,
          crops: [],
          isUngrouped: false
        })
      }
      grouped.get(key).crops.push(crop)
    }
  }

  // Convert Map to array and append ungrouped items
  return [...grouped.values(), ...ungrouped]
}

/**
 * Calculate extra vertical space needed for succession sub-rows below a crop
 *
 * @param {Object} crop - Crop object
 * @param {boolean} isExpanded - Whether the main crop row is expanded
 * @param {Object|null} cfg - Succession config object
 * @returns {number} Extra height in pixels
 */
export function getSuccessionAreaHeight(crop, isExpanded, cfg) {
  if (!cfg?.showSuccessions || crop.isOrgTaskRow) return 0
  const s = cfg.successionMap?.get(crop.id)
  if (!s) return 0
  const N = s.count
  const heightMultiplier = cfg.viewStrategy === 'both' ? 2 : 1
  if (isExpanded) return N * cfg.SUCCESSION_EXPANDED_HEIGHT * heightMultiplier
  return N * (cfg.SUCCESSION_THIN_HEIGHT + cfg.SUCCESSION_THIN_GAP)
}

/**
 * Calculate Y positions for all crops accounting for group gaps
 *
 * @param {Array} groups - Array of group objects from groupCropsByStrategy
 * @param {Array} expandedIds - Array of expanded crop IDs
 * @param {boolean} forceAllExpand - Force all crops to be expanded
 * @param {number} COLLAPSED_ROW_HEIGHT - Height of collapsed row
 * @param {number} EXPANDED_ROW_HEIGHT - Height of expanded row
 * @param {number} ROW_GAP - Gap between rows (within group)
 * @param {number} GROUP_GAP - Gap between groups (2x row gap)
 * @param {number} GROUP_HEADER_HEIGHT - Height of group header banner
 * @param {string} viewStrategy - 'phases' | 'tasks' | 'both' (doubles row height when 'both')
 * @param {Object|null} successionConfig - Optional succession layout config
 * @returns {Map} Map of cropId -> { y, groupId, isGroupStart, isExpanded }
 */
export function calculateGroupRowPositions(
  groups,
  expandedIds,
  forceAllExpand,
  COLLAPSED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT,
  ROW_GAP,
  GROUP_GAP,
  GROUP_HEADER_HEIGHT,
  viewStrategy = 'phases',
  successionConfig = null
) {
  const positions = new Map()
  let currentY = 0

  // Double row height when showing both phases and tasks
  const isBoth = viewStrategy === 'both'

  for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
    const group = groups[groupIdx]

    // Add group gap before each group (except first)
    if (groupIdx > 0) {
      currentY += GROUP_GAP
    }

    // Add group header height for actual groups (not ungrouped items)
    if (!group.isUngrouped) {
      currentY += GROUP_HEADER_HEIGHT
    }

    // Track if this is the first crop in the group
    let isFirstInGroup = true

    for (const crop of group.crops) {
      const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
      const baseRowHeight = isExpanded ? EXPANDED_ROW_HEIGHT : COLLAPSED_ROW_HEIGHT
      const heightMultiplier = isBoth && !crop.isOrgTaskRow ? 2 : 1
      const rowHeight = baseRowHeight * heightMultiplier

      positions.set(crop.id, {
        y: currentY,
        groupId: group.groupId,
        isGroupStart: isFirstInGroup,
        isExpanded,
        groupName: group.groupName,
        isUngrouped: group.isUngrouped
      })

      currentY += rowHeight + getSuccessionAreaHeight(crop, isExpanded, successionConfig)
      isFirstInGroup = false
    }
  }

  return positions
}

/**
 * Calculate total height of all rows including gaps
 *
 * @param {Array} groups - Array of group objects
 * @param {Array} expandedIds - Array of expanded crop IDs
 * @param {boolean} forceAllExpand - Force all crops to be expanded
 * @param {number} COLLAPSED_ROW_HEIGHT - Height of collapsed row
 * @param {number} EXPANDED_ROW_HEIGHT - Height of expanded row
 * @param {number} ROW_GAP - Gap between rows
 * @param {number} GROUP_GAP - Gap between groups
 * @param {number} GROUP_HEADER_HEIGHT - Height of group header banner
 * @param {string} viewStrategy - 'phases' | 'tasks' | 'both' (doubles row height when 'both')
 * @param {Object|null} successionConfig - Optional succession layout config
 * @returns {number} Total height in pixels
 */
export function calculateTotalRowsHeight(
  groups,
  expandedIds,
  forceAllExpand,
  COLLAPSED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT,
  ROW_GAP,
  GROUP_GAP,
  GROUP_HEADER_HEIGHT,
  viewStrategy = 'phases',
  successionConfig = null
) {
  let totalHeight = 0

  // Double row height when showing both phases and tasks
  const heightMultiplier = viewStrategy === 'both' ? 2 : 1

  for (let groupIdx = 0; groupIdx < groups.length; groupIdx++) {
    const group = groups[groupIdx]

    // Add group gap before each group (except first)
    if (groupIdx > 0) {
      totalHeight += GROUP_GAP
    }

    // Add group header height for actual groups (not ungrouped items)
    if (!group.isUngrouped) {
      totalHeight += GROUP_HEADER_HEIGHT
    }

    // Add height for each crop in the group
    for (let cropIdx = 0; cropIdx < group.crops.length; cropIdx++) {
      const crop = group.crops[cropIdx]
      const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
      const baseRowHeight = isExpanded ? EXPANDED_ROW_HEIGHT : COLLAPSED_ROW_HEIGHT
      totalHeight += baseRowHeight * heightMultiplier
      totalHeight += getSuccessionAreaHeight(crop, isExpanded, successionConfig)

      // Add row gap after each crop except the last in the group
      if (cropIdx < group.crops.length - 1) {
        totalHeight += ROW_GAP
      }
    }
  }

  return totalHeight
}
